import { createStore } from "zustand/vanilla"
import {
  Point, Rect, Size, centerPan, clampPan, clampZoom, screenToWorld, zoomAt,
} from "../lib/viewport"

/** Room left around what's fitted in the view, in screen pixels. */
const FIT_MARGIN_PX = 40

export type ZoomOptions = {
  min: number
  max: number
  /** Factor applied by zoom in / zoom out. */
  step: number
}

export type ViewportState = {
  zoom: number
  pan: Point
  zoomOptions: ZoomOptions
  container: HTMLElement | null
  /** Screen pixels per content unit at 100%. */
  scale: number
  /** The content's size in canvas pixels, at 100%. */
  contentSize: Size
  /** Room kept around the content when panning, in canvas pixels. */
  padding: number
  /** Space is held: a left-drag pans instead of selecting or moving. */
  spaceHeld: boolean
  setSpaceHeld: (spaceHeld: boolean) => void
  setContainer: (container: HTMLElement | null) => void
  /** The content's size and the room kept around it, in content units. */
  setContent: (contentSize: Size, padding: number) => void
  setZoom: (zoom: number) => void
  /** Sets both, the pan kept within the content's padding. */
  setView: (zoom: number, pan: Point) => void
  panBy: (delta: Point) => void
  /** Zooms to `nextZoom` keeping the point under `focal` (container coordinates) in place. */
  zoomTo: (nextZoom: number, focal: Point) => void
  zoomIn: () => void
  zoomOut: () => void
  /** Back to 100%, centred. */
  resetZoom: () => void
  /** Zooms and pans so `rect` (content units, the whole content by default) fills the view. */
  zoomToFit: (rect?: Rect) => void
  /** A point on screen in content units, or null before the canvas is mounted. */
  screenToContent: (point: Point) => Point | null
}

const containerSize = (container: HTMLElement): Size => ({
  width: container.clientWidth,
  height: container.clientHeight,
})

export const createViewportStore = (
  zoomOptions: ZoomOptions,
  scale = 1,
) => createStore<ViewportState>()(
  (set, get) => {
    const clamp = (pan: Point, zoom: number) => {
      const { container, contentSize, padding } = get()
      if (!container) return pan

      return clampPan(pan, zoom, containerSize(container), contentSize, padding)
    }

    const zoomTowardsCentre = (nextZoom: number) => {
      const { container, zoom } = get()
      if (!container || nextZoom === zoom) return

      get().zoomTo(nextZoom, { x: container.clientWidth / 2, y: container.clientHeight / 2 })
    }

    return {
      zoom: 1,
      pan: { x: 0, y: 0 },
      zoomOptions,
      container: null,
      scale,
      contentSize: { width: 0, height: 0 },
      padding: 0,
      spaceHeld: false,
      setSpaceHeld: (spaceHeld) => set({ spaceHeld }),
      setContainer: (container) => set({ container }),
      setContent: (contentSize, padding) => set({
        contentSize: { width: contentSize.width * scale, height: contentSize.height * scale },
        padding: padding * scale,
      }),
      setZoom: (zoom) => set({ zoom }),
      setView: (zoom, pan) => set({ zoom, pan: clamp(pan, zoom) }),
      panBy: (delta) => {
        const { pan, zoom } = get()
        set({ pan: clamp({ x: pan.x + delta.x, y: pan.y + delta.y }, zoom) })
      },
      zoomTo: (nextZoom, focal) => {
        const { pan, zoom } = get()
        set({ pan: clamp(zoomAt(pan, zoom, nextZoom, focal), nextZoom), zoom: nextZoom })
      },
      zoomIn: () => {
        const { zoom, zoomOptions: { max, step } } = get()
        zoomTowardsCentre(Math.min(max, zoom * step))
      },
      zoomOut: () => {
        const { zoom, zoomOptions: { min, step } } = get()
        zoomTowardsCentre(Math.max(min, zoom / step))
      },
      resetZoom: () => {
        const { container, contentSize } = get()
        set({ zoom: 1 })
        if (container) {
          set({ pan: clamp(centerPan(containerSize(container), contentSize), 1) })
        }
      },
      zoomToFit: (rect) => {
        const {
          container, contentSize, zoomOptions: { min, max },
        } = get()
        if (!container) return

        const target = rect
          ? {
            x: rect.x * scale,
            y: rect.y * scale,
            width: rect.width * scale,
            height: rect.height * scale,
          }
          : {
            x: 0, y: 0, width: contentSize.width, height: contentSize.height,
          }
        const view = containerSize(container)
        const fitted = Math.min(
          (view.width - FIT_MARGIN_PX * 2) / Math.max(target.width, 1),
          (view.height - FIT_MARGIN_PX * 2) / Math.max(target.height, 1),
        )
        const zoom = clampZoom(fitted, min, max)
        set({
          zoom,
          pan: {
            x: (view.width - target.width * zoom) / 2 - target.x * zoom,
            y: (view.height - target.height * zoom) / 2 - target.y * zoom,
          },
        })
      },
      screenToContent: (point) => {
        const { container, pan, zoom } = get()
        if (!container) return null
        const rect = container.getBoundingClientRect()

        const world = screenToWorld(point, { x: rect.left, y: rect.top }, pan, zoom)

        return { x: world.x / scale, y: world.y / scale }
      },
    }
  },
)

export type ViewportStore = ReturnType<typeof createViewportStore>
